import { getAppVersionFromUserAgent, getPlatform } from "@/utils/common"
enum TripsType {
  normal = 0,
  lccOrAcc = 1,
  ngp = 2
}

const PROP_DICT = {
  totalMileage: '行驶里程(km)',
  totalAdMileage: '辅助驾驶里程(km)',
  totalDuration: '行程时长(h)',
  totalNgpMileage: '导航辅助驾驶(km)',
  totalAccLccMileage: 'ACC/LCC(km)',
  apaTimes: '自动泊车(次)',
  vpaTimes: '记忆泊车(次)',
  driveMileage: '行驶里程(km)',
  adMileage: '辅助驾驶里程(km)',
  tripDuration: '行程时长',
  avgSpeed: '平均车速(km/h)',
  maxSpeed: '最高车速(km/h)',
  avgEnergyPowerByDistance: '百公里能耗(kWh)',
  avgEnergyElectricByDistance: '百公里电耗(kWh)',
  avgFuelConsumptionByDistance: '百公里油耗(L)',
  ngpMileage: '导航辅助驾驶(km)',
  accLccMileage: 'ACC/LCC(km)'
}
// 智能驾驶数据字典
const HIGHTLIGHT_DATA_MAP = new Map([
  ['zeroTakeOverCount', { label: '0干预行程', unit: '次' }],
  ['handleCongestionCount', { label: '应对拥堵', unit: '次' }],
  ['brakeAndPowerSupportCount', { label: '少踩电门刹车', unit: '次' }],
  ['laneChangeCount', { label: '变道', unit: '次' }],
  ['intersectionPassCount', { label: '通过路口', unit: '次' }],
  ['detourCount', { label: '绕行', unit: '次' }],
  ['rampCount', { label: '汇入汇出', unit: '次' }],
  ['uTurnCount', { label: '掉头', unit: '次' }],
  ['roundaboutCount', { label: '环岛', unit: '次' }],
  ['parkGatePassCount', { label: '闸机通行', unit: '次' }],
  ['etcCount', { label: '通过ETC', unit: '次' }],
  ['threePointTurnCount', { label: '三点掉头', unit: '次' }],
  ['aebTriggerCount', { label: 'AEB触发', unit: '次' }],
  ['aesTriggerCount', { label: 'AES触发', unit: '次' }]
])

const PARKING_DATA_MAP = new Map([
  ['apaTimes', { label: '辅助泊车', unit: '次' }],
  ['vpaTimes', { label: '记忆泊车', unit: '次' }]
])

interface HightLightDataType {
  zeroTakeOverCount: number // 0干预行程
  handleCongestionCount: number // 应对拥堵
  brakeAndPowerSupportCount: number // 少踩电门刹车
  laneChangeCount: number // 变道次数
  intersectionPassCount: number //通过路口
  detourCount: number // 绕行次数
  rampCount: number // 汇入汇出
  uTurnCount: number // 掉头次数
  roundaboutCount: number //环岛次数
  parkGatePassCount: number // 闸机通行
  etcCount: number // 通过ETC
  threePointTurnCount: number //三点掉头
  aebTriggerCount: number // AEB触发
  aesTriggerCount: number // AES触发
}

// 行驶时长，单位为xmin或者x.xh
//  行驶时长四舍五入规则：
//  - 0s<时长≤60s，显示1min
//  - 60s<时长<3570s，单位为min，时长除以60后，整数部分为min，余数大于等于30s加1min，小于30s舍弃
//  - 3570s<=时长<3600s，显示为1.0h，而不是60min
//  - 时长>=3600s，单位为h，时长除以3600后，四舍五入保留一位小数，即x.xh
const formatTripDuration = (duration: number) => {
  const durationData: { time: string | number , unit: string } = {
    time: '--',
    unit: ''
  }
  if (duration <= 0 || isNaN(duration)) {
    return durationData
  }
  if (duration <= 60) {
    durationData.time = 1
    durationData.unit = 'min'
  } else if (duration < 3570) {
    const intMins = Math.floor(duration / 60)
    const remainder = duration % 60
    durationData.time = intMins + (remainder > 30 ? 1 : 0)
    durationData.unit = 'min'
  } else if (duration < 3600) {
    durationData.time = 1.0
    durationData.unit = 'h'
  } else {
    durationData.time = (duration / 3600).toFixed(1)
    durationData.unit = 'h'
  }
  return durationData
}

function getDaysApart(startTime: number, endTime: number): number {
  if (!startTime || !endTime) {
    return 0;
  }
  // 将秒级时间戳转换为毫秒级（JavaScript Date使用毫秒）
  const startDate = new Date(startTime * 1000);
  const endDate = new Date(endTime * 1000);
  
  // 设置时间为当天零点
  const startOfStartDay = new Date(
      startDate.getFullYear(), 
      startDate.getMonth(), 
      startDate.getDate(), 
      0, 0, 0
  );
  
  const startOfEndDay = new Date(
      endDate.getFullYear(), 
      endDate.getMonth(), 
      endDate.getDate(), 
      0, 0, 0
  );
  
  // 计算两个零点时间的时间差（毫秒）
  const timeDiff = startOfEndDay.getTime() - startOfStartDay.getTime();
  
  // 将时间差转换为天数（一天 = 24 * 60 * 60 * 1000 毫秒）
  return Math.floor(timeDiff / (24 * 60 * 60 * 1000));
}

function isSupportLocationDisplay() {
  const appVersion = getAppVersionFromUserAgent()
  const platform = getPlatform()
  const [major, minor, patch] = appVersion
  let isSupportLocation = false
  const validVersion = [5, 10, 0]
  const validPlatform = ['android', 'iOS']
    if (validPlatform.includes(platform)) {
    isSupportLocation = major > validVersion[0] || 
              (major === validVersion[0] && (minor > validVersion[1] || 
              (minor === validVersion[1] && patch >= validVersion[2])))
  }
  return isSupportLocation
}
// 处理返回值为数字
function formatValueToNumber(input: any): number {
  // 尝试转换为数字
  const num = Number(input);
  
  // 检查是否为有效数字（非 NaN）
  if (isNaN(num)) {
    return 0
  }
  
  // 处理负数（包括负整数和负浮点数）
  if (num < 0) {
    return 0
  }
  
  // 处理正整数
  if (Number.isInteger(num)) {
    return num
  }
  
  // 处理正浮点数（保留一位小数，四舍五入）
  return parseFloat(num.toFixed(1))
}

// 处理高光时刻返回数据为数组
const formatHighlightData = (highlightData: HightLightDataType | undefined, needLength: number) => {
  const highlightDataArr: { label: string, value: number}[] = []
  const intelligentKeyList: string[] = Array.from(HIGHTLIGHT_DATA_MAP.keys())
  intelligentKeyList.forEach((key: string) => {
    const value = formatValueToNumber(highlightData?.[key])
    value && highlightDataArr.push({
      label: `${HIGHTLIGHT_DATA_MAP.get(key)?.label}(${HIGHTLIGHT_DATA_MAP.get(key)?.unit})`,
      value
    })
  })
  // 如果大于4条数据，则只保留前4条数据
  highlightDataArr.length > needLength  && highlightDataArr.splice(needLength)
  return highlightDataArr
}

const formatAdParkingData = (parkingData: {
  apaTimes: number,
  vpaTimes: number
}) => {
  const adParkingDataArr: { label: string, value: number}[] = []
  const adParkingKeyList: string[] = Array.from(PARKING_DATA_MAP.keys())
    adParkingKeyList.forEach((key: string) => {
    const value = formatValueToNumber(parkingData?.[key])
    value && adParkingDataArr.push({
      label: `${PARKING_DATA_MAP.get(key)?.label}(${PARKING_DATA_MAP.get(key)?.unit})`,
      value
    })
  })
  return adParkingDataArr
}

export {
  TripsType,
  type HightLightDataType,
  PROP_DICT,
  formatTripDuration,
  getDaysApart,
  // INTELLIGENT_DRIVE_DATA,
  formatHighlightData,
  formatAdParkingData,
  isSupportLocationDisplay
}